import { SxProps } from '@mui/material';
import { Theme } from '@mui/material/styles';
import { brandAlpha, brandColors, eyebrowPill, hoverOnly } from '../../../theme/mixins';

export const pageWrapperStyles: SxProps<Theme> = {
    py: { xs: 5, md: 8 },
};

export const headerBoxStyles: SxProps<Theme> = {
    mb: { xs: 4, md: 6 },
};

export const eyebrowStyles: SxProps<Theme> = eyebrowPill;

export const titleStyles: SxProps<Theme> = {
    fontWeight: 700,
    letterSpacing: '-0.015em',
    mb: 1.5,
};

export const lastUpdatedStyles: SxProps<Theme> = {
    color: 'text.secondary',
    mb: 3,
};

/**
 * The document column, capped well below the layout's `lg` container.
 *
 * Long prose set the full width of the page is close to unreadable; ~70 characters a line
 * is the usual ceiling, which at this body size lands around 68rem/2. The cap is in `ch`
 * so it tracks the font rather than a guess in pixels.
 */
export const documentStyles: SxProps<Theme> = {
    maxWidth: '72ch',
};

export const introStyles: SxProps<Theme> = {
    fontSize: '1.05rem',
    lineHeight: 1.7,
    color: 'text.secondary',
};

export const tocCardStyles: SxProps<Theme> = {
    p: { xs: 2.5, md: 3 },
    mb: { xs: 4, md: 6 },
    borderRadius: 3,
    backgroundColor: brandAlpha('primary', 0.04),
    border: '1px solid',
    borderColor: brandAlpha('primary', 0.12),
};

export const tocTitleStyles: SxProps<Theme> = {
    fontWeight: 700,
    mb: 1.5,
};

/**
 * `min(280px, 100%)` rather than a bare `280px`: a `minmax` floor is a hard floor, and on
 * a 320px phone a bare one pushes the page sideways.
 */
export const tocListStyles: SxProps<Theme> = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(min(280px, 100%), 1fr))',
    columnGap: 3,
    rowGap: 0.75,
    listStyle: 'none',
    p: 0,
    m: 0,
};

export const tocLinkStyles: SxProps<Theme> = {
    color: 'text.primary',
    textDecoration: 'none',
    fontSize: '0.95rem',
    ...hoverOnly({
        color: brandColors.primary,
        textDecoration: 'underline',
    }),
};

export const sectionStyles: SxProps<Theme> = {
    mb: { xs: 4, md: 5 },
    // So an anchored heading does not end up under the sticky header.
    scrollMarginTop: '96px',
};

export const sectionTitleStyles: SxProps<Theme> = {
    fontWeight: 700,
    letterSpacing: '-0.01em',
    mb: 2,
};

export const paragraphStyles: SxProps<Theme> = {
    lineHeight: 1.75,
    mb: 2,
    color: 'text.primary',
};

/** `listStyleType` is explicit: MUI's `List` is a `<ul>` with the browser bullet stripped. */
export const listStyles: SxProps<Theme> = {
    pl: 3,
    mb: 2,
    listStyleType: 'disc',
};

export const listItemStyles: SxProps<Theme> = {
    display: 'list-item',
    lineHeight: 1.75,
    mb: 1,
    color: 'text.primary',
};

export const linkListStyles: SxProps<Theme> = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 1,
    mb: 2,
};

/**
 * Two columns on anything but a phone, and `max-content` on the labels so the values line
 * up as a proper definition list rather than a ragged one.
 */
export const operatorGridStyles: SxProps<Theme> = {
    display: 'grid',
    gridTemplateColumns: { xs: '1fr', sm: 'max-content 1fr' },
    columnGap: 3,
    rowGap: 1,
    mb: 3,
    p: { xs: 2, md: 2.5 },
    borderRadius: 2,
    backgroundColor: brandAlpha('primary', 0.04),
};

/** So a label/value pair sits in the parent grid rather than in a box of its own. */
export const operatorRowStyles: SxProps<Theme> = {
    display: 'contents',
};

export const operatorLabelStyles: SxProps<Theme> = {
    fontWeight: 600,
    color: 'text.secondary',
};

export const operatorValueStyles: SxProps<Theme> = {
    color: 'text.primary',
};

export const placeholderAlertStyles: SxProps<Theme> = {
    mb: 4,
};
